import { useEffect, useRef, useState } from "react";

interface LazyMapProps {
  src: string;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  height?: number | string;
}

/**
 * Defers loading the Google Maps embed iframe until it scrolls near the viewport.
 * This prevents ~200KB of Maps API JS from being downloaded on initial page load,
 * improving LCP and reducing unused JavaScript.
 */
export default function LazyMap({
  src,
  title = "Map",
  className,
  style,
  height = 450,
}: LazyMapProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldLoad]);

  return (
    <div ref={ref} style={{ minHeight: typeof height === "number" ? `${height}px` : height }}>
      {shouldLoad ? (
        <iframe
          src={src}
          title={title}
          width="100%"
          height={height}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          className={className}
          style={style}
        />
      ) : null}
    </div>
  );
}
